import React, { useState } from 'react';
import {
  Upload,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Image as ImageIcon,
  X,
  Plus
} from 'lucide-react';
import vercelBlobService from '../services/vercelBlobService';
import firebaseMetadataService from '../services/firebaseMetadataService';

const VercelBlobUploader = ({ onUploadComplete, onClose }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);
  const [productData, setProductData] = useState({
    title: '',
    description: '',
    category: '',
    price: '',
    inStock: true,
    featured: false
  });
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategory, setNewCategory] = useState('');

  const categories = [
    'Arduino & Development Boards',
    'Sensors & Modules',
    'Display & Interface Modules',
    'Communication Modules',
    'Motors & Motor Drivers',
    'Relay Modules & Power Control',
    'Power Supply & Voltage Regulators',
    'Breadboards, Kits & Misc',
    'Other Components'
  ];

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    setSelectedFiles(prev => [...prev, ...imageFiles]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddNewCategory = () => {
    if (newCategory.trim()) {
      setProductData(prev => ({ ...prev, category: newCategory.trim() }));
      setNewCategory('');
      setShowNewCategoryInput(false);
    }
  };

  const handleCategoryChange = (value) => {
    if (value === 'ADD_NEW') {
      setShowNewCategoryInput(true);
      setNewCategory('');
    } else {
      setProductData(prev => ({ ...prev, category: value }));
      setShowNewCategoryInput(false);
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      alert('Please select at least one image file');
      return;
    }

    if (!productData.title.trim()) {
      alert('Please enter a product title');
      return;
    }

    setUploading(true);
    setUploadResults([]);

    const results = [];

    for (const file of selectedFiles) {
      try {
        console.log(`📤 Uploading ${file.name} to Vercel Blob...`);

        // Create unique filename with timestamp to avoid conflicts
        const timestamp = Date.now();
        const fileExtension = file.name.split('.').pop().toLowerCase();
        const sanitizedTitle = productData.title
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '');
        const filename = `products/${sanitizedTitle}-${timestamp}.${fileExtension}`;

        // Upload to Vercel Blob using the direct uploadFile method
        const uploadResult = await vercelBlobService.uploadFile(file, filename, {
          contentType: file.type
        });

        if (uploadResult.success) {
          // Save metadata to Firebase
          const filenameOnly = filename.split('/').pop();
          await firebaseMetadataService.saveImageMetadata(filenameOnly, {
            title: productData.title,
            description: productData.description,
            category: productData.category,
            price: productData.price ? parseFloat(productData.price) : null,
            currency: 'K',
            in_stock: productData.inStock,
            featured: productData.featured,
            storage_provider: 'vercel-blob',
            vercel_blob_url: uploadResult.url,
            vercel_blob_pathname: uploadResult.pathname,
            vercel_blob_download_url: uploadResult.downloadUrl,
            vercel_blob_size: uploadResult.size,
            vercel_blob_uploaded_at: uploadResult.uploadedAt,
            public_url: uploadResult.url,
            file_path: uploadResult.pathname,
            publicUrl: uploadResult.url,
            fullPath: uploadResult.pathname,
            uploaded_at: new Date().toISOString()
          });

          results.push({
            filename: file.name,
            success: true,
            url: uploadResult.url,
            message: 'Uploaded successfully to Vercel Blob with global CDN'
          });

          console.log(`✅ Successfully uploaded ${file.name} to Vercel Blob`);
        } else {
          results.push({
            filename: file.name,
            success: false,
            error: uploadResult.error,
            message: 'Upload failed'
          });

          console.error(`❌ Failed to upload ${file.name}:`, uploadResult.error);
        }
      } catch (error) {
        console.error(`❌ Error uploading ${file.name}:`, error);
        results.push({
          filename: file.name,
          success: false,
          error: error.message,
          message: 'Upload error'
        });
      }
    }

    setUploadResults(results);
    setUploading(false);

    // Call completion callback if provided
    if (onUploadComplete) {
      onUploadComplete(results);
    }

    // Clear form if all uploads successful
    const allSuccessful = results.every(r => r.success);
    if (allSuccessful) {
      setSelectedFiles([]);
      setProductData({
        title: '',
        description: '',
        category: '',
        price: '',
        inStock: true,
        featured: false
      });
      setShowNewCategoryInput(false);
      setNewCategory('');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Upload className="mr-2" size={24} />
            Upload to Vercel Blob
          </h2>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          )}
        </div>

        <div className="p-6 space-y-6">
          {/* Product Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Product Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Title *
                </label>
                <input
                  type="text"
                  value={productData.title}
                  onChange={(e) => setProductData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Arduino Uno R3"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                {showNewCategoryInput ? (
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      placeholder="Enter new category name"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddNewCategory();
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleAddNewCategory}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
                    >
                      <Plus size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewCategoryInput(false);
                        setNewCategory('');
                      }}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <select
                    value={productData.category}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select category</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                    <option value="ADD_NEW" className="font-semibold text-blue-600">
                      ➕ Add New Category
                    </option>
                  </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price (Kwacha)
                </label>
                <input
                  type="number"
                  value={productData.price}
                  onChange={(e) => setProductData(prev => ({ ...prev, price: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="750"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={productData.inStock}
                    onChange={(e) => setProductData(prev => ({ ...prev, inStock: e.target.checked }))}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">In Stock</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={productData.featured}
                    onChange={(e) => setProductData(prev => ({ ...prev, featured: e.target.checked }))}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Featured</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={productData.description}
                onChange={(e) => setProductData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="3"
                placeholder="Product description..."
              />
            </div>
          </div>

          {/* File Selection */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Select Images</h3>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                <span className="mt-2 block text-sm font-medium text-gray-900">
                  Click to select images
                </span>
                <span className="mt-1 block text-sm text-gray-500">
                  PNG, JPG, GIF up to 10MB each
                </span>
              </label>
            </div>

            {/* Selected Files */}
            {selectedFiles.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700">
                  Selected Files ({selectedFiles.length})
                </h4>
                <div className="max-h-32 overflow-y-auto space-y-2">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <div className="flex items-center">
                        <ImageIcon size={16} className="text-gray-400 mr-2" />
                        <span className="text-sm text-gray-700">{file.name}</span>
                        <span className="text-xs text-gray-500 ml-2">
                          ({formatFileSize(file.size)})
                        </span>
                      </div>
                      <button
                        onClick={() => removeFile(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Upload Results */}
          {uploadResults.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Upload Results</h4>
              <div className="max-h-32 overflow-y-auto space-y-2">
                {uploadResults.map((result, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                    <div className="flex items-center">
                      {result.success ? (
                        <CheckCircle size={16} className="text-green-500 mr-2" />
                      ) : (
                        <XCircle size={16} className="text-red-500 mr-2" />
                      )}
                      <span className="text-sm text-gray-700">{result.filename}</span>
                    </div>
                    <span className={`text-xs ${result.success ? 'text-green-600' : 'text-red-600'}`}>
                      {result.message}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Button */}
          <div className="flex justify-end space-x-3">
            {onClose && (
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                disabled={uploading}
              >
                Cancel
              </button>
            )}
            <button
              onClick={handleUpload}
              disabled={uploading || selectedFiles.length === 0 || !productData.title.trim()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {uploading ? (
                <>
                  <RefreshCw className="mr-2 animate-spin" size={16} />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="mr-2" size={16} />
                  Upload to Vercel Blob
                </>
              )}
            </button>
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-blue-400 mr-2 mt-0.5" />
              <div className="text-sm text-blue-700">
                <p className="font-medium">Vercel Blob Benefits:</p>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>Global CDN with instant delivery</li>
                  <li>Automatic image optimization</li>
                  <li>5GB free storage + 100GB bandwidth</li>
                  <li>Enterprise security and 99.9% uptime</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VercelBlobUploader;
